import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Translate from "../../utils/Translate";
import { useApi } from "../../utils/Api";
import AutocompleteSearchField from "../AutocompleteSearchField";
import ActorAutocompleteOption from "../ActorAutocompleteOption";
import { actorCompanyTypes, actorSearch } from "../../utils/actorSearch";
import { AlertDialog } from "../AlertDialog";
import { Computer, Error } from "@mui/icons-material";

export default function SpecialPriceDialog({
  specialPrice: specialPriceProp,
  specialPrices: specialPricesProp,
  onClose,
  onSaved,
}) {
  const isUsingBothSingleAndMultiProps = specialPriceProp && specialPricesProp;
  const hasNeitherSingleNorMultiProp = !specialPriceProp && !specialPricesProp;
  const hasEmptyMultiProp = !!specialPricesProp && specialPricesProp.length === 0;
  const hasUnsupportedMultiProp =
    !!specialPricesProp &&
    specialPricesProp.some(
      (sp) => sp.partId !== specialPricesProp[0].partId || sp.currency !== specialPricesProp[0].currency || !sp.actorId,
    );
  if (isUsingBothSingleAndMultiProps || hasNeitherSingleNorMultiProp || hasEmptyMultiProp || hasUnsupportedMultiProp) {
    throw new Error("Invalid props");
  }

  const [specialPrice, setSpecialPrice] = useState(specialPriceProp ? { ...specialPriceProp } : null);
  const [specialPrices, setSpecialPrices] = useState(
    specialPricesProp ? specialPricesProp.map((sp) => ({ ...sp, fixedPercent: null, fixedPrice: null })) : null,
  );
  const [currencies, setCurrencies] = useState([]);
  const [partPrices, setPartPrices] = useState([]);
  const [isFixedPriceDiscount, setIsFixedPriceDiscount] = useState(
    specialPriceProp ? specialPriceProp.fixedPrice !== null && specialPriceProp.fixedPrice !== undefined : false,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [longErrorMessage, setLongErrorMessage] = useState(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const api = useApi();

  const companySearch = useCallback(async (searchText) => {
    var foundActors = await actorSearch(
      searchText,
      actorCompanyTypes,
      ["ActorId", "ActorName", "OrgNo", "ActorEmail", "ExternalActorNo", "ExternalDataId"],
      50,
    );
    if (foundActors) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        externalDataId: actor.externalDataId,
        observationNote: actor.observationNote,
      }));
    }
  }, []);

  const partSearch = useCallback(
    async (searchText) => {
      const response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}warehouse/parts/search?q=${searchText}&onlySellable=false`,
        false,
        "GET",
      );
      if (response.isSuccessful) {
        return response.parts;
      }
    },
    [api],
  );

  const handleCompanyChanged = useCallback(
    async (actor) => {
      setSpecialPrice((prev) => ({ ...prev, actorId: actor?.actorId, actorName: actor?.actorName }));

      if (!specialPriceProp.currency && actor) {
        await loadDefaultCurrency(actor.actorId);
      }
    },
    [specialPriceProp],
  );

  const loadPartPrices = useCallback(
    async (partId) => {
      const response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}warehouse/parts/${partId}/prices`,
        false,
        "GET",
      );
      if (response.isSuccessful) {
        setPartPrices(response.value);
      }
    },
    [api],
  );

  const loadCurrencies = useCallback(async () => {
    const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}orders/currencies`);
    setCurrencies(data);
  }, [api]);

  const loadDefaultCurrency = useCallback(
    async (actorId) => {
      const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/${actorId}/orderinfo`, false, "GET");
      if (response.isSuccessful) {
        setSpecialPrice((prev) => ({ ...prev, currency: response.defaultCurrency }));
      }
    },
    [api],
  );

  useEffect(() => {
    async function initSingleSpecialPrice() {
      setIsLoading(true);
      await loadCurrencies();

      if (specialPriceProp.actorId && !specialPriceProp.currency) {
        await loadDefaultCurrency(specialPriceProp.actorId);
      }

      if (specialPriceProp.partId) {
        await loadPartPrices(specialPriceProp.partId);
      }

      setIsLoading(false);
    }

    async function initMultiSpecialPrices() {
      setIsLoading(true);
      await loadCurrencies();
      await loadPartPrices(specialPricesProp[0].partId);
      setIsLoading(false);
    }

    if (specialPriceProp) {
      initSingleSpecialPrice();
    } else {
      initMultiSpecialPrices();
    }
  }, [specialPriceProp, specialPricesProp, loadCurrencies, loadPartPrices]);

  const handlePartSelected = useCallback(
    async (selectedPart) => {
      if (selectedPart) {
        setSpecialPrice((prev) => ({
          ...prev,
          partId: selectedPart.partId,
          partNo: selectedPart.partNo,
          partDescription: selectedPart.description,
        }));

        await loadPartPrices(selectedPart.partId);
      }
    },
    [loadPartPrices],
  );

  const getCustomerPrice = useCallback(() => {
    if (isFixedPriceDiscount) {
      return (specialPrice ?? specialPrices[0]).fixedPrice;
    } else {
      const currency = (specialPrice ?? specialPrices[0]).currency;
      const originalPrice = partPrices.find((pp) => pp.currency === currency)?.price ?? 0;
      const discount = (specialPrice ?? specialPrices[0]).fixedPercent ?? 0;
      return (1 - discount) * originalPrice;
    }
  }, [specialPrice, specialPrices, partPrices, isFixedPriceDiscount]);

  const getDefaultPrice = useCallback(
    () =>
      (specialPrice ?? specialPrices[0]).currency
        ? (partPrices.find((pp) => pp.currency === (specialPrice ?? specialPrices[0]).currency)?.price ?? 0)
        : 0,
    [specialPrice, specialPrices, partPrices],
  );

  const handleIsFixedPriceChange = useCallback(
    (isFixedPrice) => {
      setIsFixedPriceDiscount(isFixedPrice);

      if (specialPriceProp) {
        setSpecialPrice((prev) => ({
          ...prev,
          fixedPrice: prev.fixedPrice === null || prev.fixedPrice === undefined ? getDefaultPrice() : prev.fixedPrice,
        }));
      } else {
        setSpecialPrices((prev) =>
          prev.map((sp) => ({
            ...sp,
            fixedPrice: sp.fixedPrice === null || sp.fixedPrice === undefined ? getDefaultPrice() : sp.fixedPrice,
          })),
        );
      }
    },
    [specialPriceProp, getDefaultPrice],
  );

  const handleFixedPriceChange = useCallback(
    (newValue) => {
      if (specialPriceProp) {
        setSpecialPrice((prev) => ({
          ...prev,
          fixedPrice: isNaN(newValue) ? 0 : newValue,
        }));
      } else {
        setSpecialPrices((prev) =>
          prev.map((sp) => ({
            ...sp,
            fixedPrice: isNaN(newValue) ? 0 : newValue,
          })),
        );
      }
    },
    [specialPriceProp],
  );

  const handleFixedPercentChange = useCallback(
    (newValue) => {
      if (specialPriceProp) {
        setSpecialPrice((prev) => ({
          ...prev,
          fixedPercent: isNaN(newValue) ? 0 : newValue / 100,
        }));
      } else {
        setSpecialPrices((prev) =>
          prev.map((sp) => ({
            ...sp,
            fixedPercent: isNaN(newValue) ? 0 : newValue / 100,
          })),
        );
      }
    },
    [specialPriceProp],
  );

  const saveSpecialPrice = useCallback(
    async (specialPrice, isMulti) => {
      const request = {
        ...specialPrice,
        fixedPrice: isFixedPriceDiscount ? specialPrice.fixedPrice : null,
        fixedPercent: isFixedPriceDiscount ? null : specialPrice.fixedPercent,
      };
      const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/special-prices`;
      const response = !isMulti
        ? await api.fetch(url, request, "POST")
        : await api.fetchWithOverride(url, request, "POST", () => true); // To avoid automatically displaying snackbar on error
      return response;
    },
    [api, isFixedPriceDiscount],
  );

  async function handleSaveSingleSpecialPrice() {
    const response = await saveSpecialPrice(specialPrice, false);
    if (response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      await onSaved();
    }
  }

  async function handleSaveMultipleSpecialPrices() {
    const results = [];

    for (let specialPrice of specialPrices) {
      const response = await saveSpecialPrice(specialPrice, true);
      results.push({ response, specialPrice });
    }

    const allOk = results.every((r) => r.response.isSuccessful);
    if (allOk) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      await onSaved();
    } else {
      const errorMessage = createLongErrorMessage(results);
      setLongErrorMessage(errorMessage);
    }
  }

  const disableDiscountSection = specialPrice
    ? !specialPrice.currency || !specialPrice.partId || !specialPrice.actorId
    : false;

  return (
    <>
      <Dialog open onClose={onClose}>
        <DialogTitle>
          {specialPriceProp && specialPriceProp.partId && specialPriceProp.actorId
            ? Translate.get("EditSpecialPrices")
            : specialPricesProp
              ? Translate.get("EditSpecialPrices")
              : Translate.get("AddSpecialPrice")}
        </DialogTitle>
        <DialogContent>
          <Grid container gap={4} marginTop={1}>
            <Grid container gap={2} size={12}>
              <Grid size={12}>
                <AutocompleteSearchField
                  disableAddAsNew
                  requireSelection
                  disabled={!!specialPriceProp?.actorId || !!specialPricesProp}
                  placeholder={Translate.get("Company")}
                  label={Translate.get("Company")}
                  value={
                    specialPrice
                      ? { actorId: specialPrice.actorId ?? "", actorName: specialPrice.actorName ?? "" }
                      : { actorId: -1, actorName: Translate.get("MultipleCompaniesSelected") } // Perhaps we could improve this with <Chip /> in the future
                  }
                  onValueChange={handleCompanyChanged}
                  getOptionLabel={(option) => option?.actorName ?? ""}
                  renderOption={(option) => (
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <ActorAutocompleteOption
                        actorName={option?.actorName}
                        orgNo={option?.orgNo}
                        email={option?.email}
                      />
                      {(option.externalDataId || option.observationNote) && (
                        <Stack
                          direction="column"
                          justifyContent={
                            option.externalDataId && option.observationNote
                              ? "space-between"
                              : option.externalDataId
                                ? "flex-start"
                                : "flex-end"
                          }
                        >
                          {option.externalDataId && (
                            <Box display="flex" width="80px" gap={1}>
                              <Computer />
                              <Box>{option.externalDataId ?? ""}</Box>
                            </Box>
                          )}
                          {option.observationNote && (
                            <Tooltip title={option.observationNote} arrow>
                              <Error sx={{ color: "red" }} />
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                    </Box>
                  )}
                  keyPropName="actorId"
                  search={
                    specialPriceProp && (!specialPriceProp.actorId || specialPriceProp.actorId <= 0)
                      ? companySearch
                      : null
                  }
                  //   textFieldProps={{
                  //     required: true,
                  //     error: !isActorNameBlank && !!errors["actorName"],
                  //     helperText: !isActorNameBlank && errors["actorName"],
                  //   }}
                  hideOptionsFilter={
                    !!specialPrice && specialPrice.actorId > 0 && ((option) => option.actorId === specialPrice.actorId)
                  }
                  textFieldProps={{ required: true }}
                />
              </Grid>

              <Grid size={12}>
                <AutocompleteSearchField
                  disableAddAsNew
                  requireSelection
                  disabled={!!specialPriceProp?.partId || !!specialPricesProp}
                  label={Translate.get("PartLabel")}
                  value={{
                    partId: (specialPrice ?? specialPrices[0]).partId ?? "",
                    description: (specialPrice ?? specialPrices[0]).partDescription ?? "",
                  }}
                  groupBy={(option) => Translate.getPartTypePlural(option.partType)}
                  textFieldProps={{ required: true }}
                  onValueChange={(selectedPart) => handlePartSelected(selectedPart)}
                  getOptionLabel={(option) => option?.description ?? ""}
                  renderOption={(option) => (
                    <Grid container>
                      <Grid size={3}>
                        <Typography
                          noWrap
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {option.partNo ?? ""}
                        </Typography>
                      </Grid>
                      <Grid size={9}>
                        <Box>{option.description}</Box>
                      </Grid>
                    </Grid>
                  )}
                  keyPropName="partId"
                  search={async (searchText) => {
                    const results = await partSearch(searchText);
                    // Sorting needed for groupBy of search results
                    return results.sort((a, b) => {
                      const partTypeSort = Translate.getPartTypePlural(a.partType).localeCompare(
                        Translate.getPartTypePlural(b.partType),
                      );
                      if (partTypeSort != 0) {
                        return partTypeSort;
                      } else if (a.partNo && b.partNo) {
                        return !isNaN(a.partNo) && !isNaN(b.partNo)
                          ? Number(a.partNo) - Number(b.partNo)
                          : a.partNo.localeCompare(b.partNo);
                      } else if (a.partNo) {
                        return 1;
                      } else if (b.partNo) {
                        return -1;
                      } else {
                        return a.description.localeCompare(b.description);
                      }
                    });
                  }}
                  //   textFieldProps={{
                  //     autoFocus: true,
                  //     required: true,
                  //     error: false,
                  //     helperText: "",
                  //   }}
                />
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel id="currency-label">{Translate.get("CurrencyLabel")}</InputLabel>
                  <Select
                    required
                    label={Translate.get("CurrencyLabel")}
                    labelId="currency-label"
                    disabled={!!specialPriceProp?.currency || !!specialPricesProp}
                    value={(specialPrice ?? specialPrices[0]).currency ?? ""}
                    onChange={(event) => setSpecialPrice((prev) => ({ ...prev, currency: event.target.value }))}
                  >
                    {currencies &&
                      currencies.map((currency) => (
                        <MenuItem value={currency.code} key={currency.code}>
                          {currency.code}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container gap={2} size={12}>
              <Grid container direction="row" justifyContent="space-between" alignItems="flex-end" size={12}>
                <Grid >
                  <h4>{Translate.get("Discount")}</h4>
                </Grid>
                <Grid >
                  <FormControlLabel
                    sx={{ marginBottom: "-10px" }}
                    control={
                      <Checkbox
                        disabled={disableDiscountSection}
                        checked={!disableDiscountSection && isFixedPriceDiscount}
                        onChange={(event) => handleIsFixedPriceChange(event.target.checked)}
                      />
                    }
                    label={Translate.get("FixedPrice")}
                  />
                </Grid>
              </Grid>

              {isFixedPriceDiscount ? (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    disabled={disableDiscountSection}
                    label={Translate.get("FixedPrice")}
                    // value={!disableDiscountSection ? specialPrice.fixedPrice ?? 0 : ""}
                    value={(specialPrice ?? specialPrices[0]).fixedPrice ?? ""}
                    onChange={(event) => handleFixedPriceChange(event.target.value)}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      type: "number",
                      min: 0,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{specialPrice?.currency}</InputAdornment>,
                    }}
                  />
                </Grid>
              ) : (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    disabled={disableDiscountSection}
                    label={Translate.get("SpecialPriceFixedPercent")}
                    value={!disableDiscountSection ? ((specialPrice ?? specialPrices[0]).fixedPercent ?? 0) * 100 : ""}
                    onChange={(event) => handleFixedPercentChange(event.target.value)}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      type: "number",
                      max: 100,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <TextField
                  fullWidth
                  disabled
                  label={
                    isFixedPriceDiscount || disableDiscountSection
                      ? Translate.get("CustomerPrice")
                      : `${Translate.get("CustomerPrice")} (${Translate.get(
                          "BasedOnCurrentOriginalPrice",
                        ).toLowerCase()})`
                  }
                  value={!disableDiscountSection ? getCustomerPrice() : ""}
                  helperText={
                    !disableDiscountSection ? `${Translate.get("OriginalPrice")}: ${getDefaultPrice()}` : null
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
          <ButtonWithSpinner
            onClick={async () =>
              specialPrice ? await handleSaveSingleSpecialPrice() : await handleSaveMultipleSpecialPrices()
            }
            variant="contained"
          >
            {Translate.get("Save")}
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
      <AlertDialog
        titleText={Translate.get("SomethingFailed")}
        bodyText={longErrorMessage}
        buttonText={Translate.get("Ok")}
        open={!!longErrorMessage}
        onClose={async () => {
          setLongErrorMessage(null);
          await onSaved();
        }}
      />
    </>
  );
}

function createLongErrorMessage(results) {
  const failedResults = results.filter((r) => !r.response.isSuccessful);
  if (failedResults.length > 0) {
    const errorMessage = failedResults
      .map(({ response, specialPrice }) => {
        const message = response.errorMessageTranslationKey
          ? Translate.get(response.errorMessageTranslationKey)
          : Translate.get("SomethingFailed");
        const part = `${specialPrice.partNo ?? ""} ${specialPrice.partDescription ?? ""} (id: ${specialPrice.partId})`;
        const actor = `${specialPrice.actorName ?? ""} (id: ${specialPrice.actorId})`;
        return `${part}, ${actor}:\n${message}`;
      })
      .join("\n\n");
    return errorMessage;
  } else {
    return null;
  }
}
